import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient.js';

// ─── One-time "new feature" tour: use-my-location ─────────
// Flow: banner at the top of the portal → blocking explanation the user must
// acknowledge → the first start-of-day travel row's address blinks → opening
// that row hands off to the crosshair button, which pulses until it is used →
// only then does the day Map button appear and bounce as the second chapter.
//
// The Map button does not exist at all until the location demo exits — however
// it exits. Only users who finished onboarding in an earlier session (stage
// null from the stored 'seen') see it without a cue.
//
// Bump the version suffix to announce a different feature later; the old key
// stays set so this tour never returns for people who already saw it.
const TOUR_KEY = 'bvm.feature.use-my-location.v1';

// Matches isStartOfDayType in RouteDetailPage — the "home/start" rows.
const START_TYPES = ['start_of_day_home', 'start_of_day_hotel', 'home_base', 'start_of_week_from_home'];

// Progress is stored in two steps so a reload resumes where the user left off
// rather than replaying the location demo:
//   (unset)         → 'banner'  — the location demo has not run
//   'location-done' → 'maps'    — demo exited; Map button visible and bouncing
//   'seen'          → null      — both chapters finished
const readProgress = () => {
  try {
    return window.localStorage.getItem(TOUR_KEY);
  } catch {
    return 'seen'; // storage unavailable — never nag
  }
};

const write = (value) => {
  try {
    window.localStorage.setItem(TOUR_KEY, value);
  } catch {
    // Not persisted; the stage still applies for this session.
  }
};

const initialStage = () => {
  const progress = readProgress();
  if (progress === 'seen') return null;
  if (progress === 'location-done') return 'maps';
  return 'banner';
};

const FeatureTourContext = createContext({
  stage: null,
  targetTravelId: null,
  setTargetTravelId: () => {},
  advance: () => {},
  complete: () => {},
  endLocationChapter: () => {},
  mapButtonVisible: true,
  mapButtonBlinks: false,
});

export const useFeatureTour = () => useContext(FeatureTourContext);

export const FeatureTourProvider = ({ children }) => {
  // null = nothing to show. Otherwise: banner → explain → find-start → crosshair → maps.
  const [stage, setStage] = useState(initialStage);
  const [targetTravelId, setTargetTravelId] = useState(null);
  const navigate = useNavigate();

  // Every exit from the location demo — finishing it, Skip, the ×, Escape —
  // lands here, so the Map button always gets its turn.
  const endLocationChapter = useCallback(() => {
    setStage('maps');
    write('location-done');
  }, []);

  const complete = useCallback(() => {
    setStage(null);
    write('seen');
  }, []);

  const advance = useCallback((next) => setStage(next), []);

  // Acknowledging the explanation sends the user to a route that actually has a
  // start-of-day row, so the thing we are pointing at is on screen.
  const beginPointing = useCallback(async () => {
    setStage('find-start');
    try {
      const { data } = await supabase
        .from('route_items')
        .select('route_id, routes!inner(status)')
        .eq('row_type', 'TRAVEL')
        .in('travel_type', START_TYPES)
        .in('routes.status', ['draft', 'active'])
        .order('day_number', { ascending: true })
        .limit(1);
      const routeId = data?.[0]?.route_id;
      navigate(routeId ? `/portal/routes/${routeId}` : '/portal/routes');
    } catch {
      navigate('/portal/routes');
    }
  }, [navigate]);

  useEffect(() => {
    if (stage !== 'explain') return undefined;
    const onKey = (e) => { if (e.key === 'Escape') endLocationChapter(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [stage, endLocationChapter]);

  // Hidden for the whole location chapter, then revealed (bouncing) as chapter
  // two. stage === null means onboarding finished in an earlier session.
  const mapButtonVisible = stage === null || stage === 'maps';
  const mapButtonBlinks = stage === 'maps';

  return (
    <FeatureTourContext.Provider
      value={{ stage, targetTravelId, setTargetTravelId, advance, complete, endLocationChapter, mapButtonVisible, mapButtonBlinks }}
    >
      {children}
      {stage === 'explain' && <FeatureTourModal onOk={beginPointing} onDismiss={endLocationChapter} />}
    </FeatureTourContext.Provider>
  );
};

/** Slim announcement bar. Renders above the page content, once per device. */
export const FeatureTourBanner = () => {
  const { stage, advance, endLocationChapter } = useFeatureTour();
  if (stage !== 'banner') return null;

  return (
    <div className="mb-4 flex items-start gap-3 px-4 py-2.5 rounded-md bg-green-600/15 border border-green-600/50">
      <span className="text-[10px] font-semibold uppercase tracking-wide bg-green-700 text-white px-1.5 py-0.5 rounded mt-0.5">
        New
      </span>
      <p className="flex-1 text-sm text-gray-300 leading-relaxed">
        You can now fill in an address with your current location instead of typing it.
      </p>
      <button
        onClick={() => advance('explain')}
        className="shrink-0 px-3 py-1 text-xs font-medium bg-green-700 text-white rounded hover:bg-green-900 transition-colors"
      >
        Show me
      </button>
      <button
        onClick={endLocationChapter}
        className="shrink-0 text-gray-400 hover:text-white text-xl leading-none"
        aria-label="Dismiss announcement"
      >
        &times;
      </button>
    </div>
  );
};

const FeatureTourModal = ({ onOk, onDismiss }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center px-4" role="dialog" aria-modal="true">
    <div className="fixed inset-0 bg-gray-900/70" />
    <div className="relative bg-gray-900 border border-gray-500/60 rounded-lg shadow-2xl max-w-md w-full p-5">
      <button
        onClick={onDismiss}
        className="absolute top-2.5 right-3 text-gray-400 hover:text-white text-xl leading-none"
        aria-label="Dismiss"
      >
        &times;
      </button>
      <p className="text-sm font-semibold text-white mb-2">New: fill an address with your location</p>
      <p className="text-xs text-gray-300 leading-relaxed">
        Start and end of day rows, transit stops, and job locations can now be
        filled in from your device&rsquo;s location instead of typing the address.
      </p>
      <p className="text-xs text-gray-300 leading-relaxed mt-2">
        Click OK and we&rsquo;ll take you to a route. The first <span className="text-green-400">home/start row</span>&rsquo;s
        address will blink — click it to open the field, then click the pulsing crosshair to drop
        your current location straight in.
      </p>
      <p className="text-xs text-gray-400 leading-relaxed mt-2">
        Your browser will ask permission the first time. Location is only read when you click.
      </p>
      <div className="flex justify-end gap-2 mt-4">
        <button
          onClick={onDismiss}
          className="px-3 py-1.5 text-xs text-gray-300 hover:text-white hover:bg-gray-500/20 rounded transition-colors"
        >
          Skip
        </button>
        <button
          onClick={onOk}
          className="px-4 py-1.5 text-xs font-medium bg-green-700 text-white rounded hover:bg-green-900 transition-colors"
        >
          OK
        </button>
      </div>
    </div>
  </div>
);

export default FeatureTourProvider;
